import React, { useEffect, useState } from 'react';
import { Row, Col, Container } from 'reactstrap';
import DashboardLayout from '../Layout/DashboardLayout';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import FuelBalance from '../Widgets/FuelBalance';
import SupplyTypeInfo from '../Widgets/SupplyTypeInfo';
import FuelTankInfo from '../Widgets/FuelTankInfo';
import HourMeterPowerGeneration from '../Widgets/HourMeterPowerGeneration';
import FuelBalancePowerGeneration from '../Widgets/FuelBalancePowerGeneration';
import HourMeterBalance from '../Widgets/HourMeterBalance';
import HourMeterConsumption from '../Widgets/HourMeterConsumption';
import FuelTankInfoBalance from '../Widgets/FuelTankInfoBalance';
import useRedirectEstablishmentActiveContent from '../../../../../Components/Hooks/useRedirectEstablishmentActiveContent';
import { useMediaQuery } from 'react-responsive';
import UiContent from '../../../../../Components/Common/UiContent';
import BreadCrumb from '../../../../../Components/Common/BreadCrumb';
import { getEstablishment } from '../../../../../slices/thunks';
import { clearDashboardData } from '../../../../../slices/fuelSupply/reducer';
import { clearDashboardData as clearFuelTankData } from '../../../../../slices/fuelTank/reducer';
import { useNavigate, useParams } from 'react-router-dom';
import FuelBalancePowerGenerationEChart from '../Widgets/FuelBalancePowerGenerationEChart';
import SupplyTypeInfoEChart from '../Widgets/SupplyTypeInfoEChart';
import HourMeterPowerGenerationEChart from '../Widgets/HourMeterPowerGenerationEChart';
import FuelTankInfoEChart from '../Widgets/FuelTankInfoEChart';
import { Grid } from '@mantine/core';
import EmissionsPowerGenerationEChart from '../Widgets/EmissionsPowerGenerationEChart';
import ConsumptionsPowerGenerationEChart from '../Widgets/ConsumptionsPowerGenerationEChart';
import HourMeterPowerGenerationSummaryEChart from '../Widgets/HourMeterPowerGenerationSummaryEChart';
import { EstablishmentCardInfo } from './EstablishmentCardInfo';
import { icon } from 'leaflet';

function useWindowSize() {
	const [size, setSize] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
		col: 0,
	});

	useEffect(() => {
		const handleResize = () => {
			const width = window.innerWidth;
			let col = 0;

			if (width >= 1400) col = 6; // xxl
			else if (width >= 1200) col = 8; // xl
			else if (width >= 992) col = 12; // lg
			else if (width >= 768) col = 10; // md
			else if (width >= 576) col = 8; // sm
			else col = 6; // xs

			setSize({
				width,
				height: window.innerHeight,
				col,
			});
		};

		window.addEventListener('resize', handleResize);
		handleResize(); // para ejecutarlo al principio

		return () => window.removeEventListener('resize', handleResize);
	}, []);

	return size;
}

const FuelDashboard = () => {
	const { id: establishmentId } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const breadcrumbLinks = [
		{ to: '#', label: 'Inicio', icon: 'icon-house' },
		{ to: '#', label: 'Abastecimiento', icon: 'icon-shopping-basket' },
		{ to: '#', label: 'Combustible', icon: 'icon-fuel' },
	];

	// Clear dashboard data only after navigation is complete to prevent animation flickering
	useEffect(() => {
		// Only clear data if we have a valid establishmentId and it's different from the active one
		if (establishmentId && establishmentId !== 'all') {
			// Small delay to ensure navigation is complete
			const timer = setTimeout(() => {
				dispatch(clearDashboardData());
				dispatch(clearFuelTankData());
			}, 100);

			return () => clearTimeout(timer);
		}
	}, [establishmentId, dispatch]);

	// get selected establishment

	const { establishmentActive } = useSelector(
		createSelector(
			state => state.Profile,
			data => ({
				establishmentActive: data.establishmentActive,
			})
		)
		// shallowEqual
	);

	useEffect(() => {
		if (establishmentActive) {
			if (establishmentId !== establishmentActive.value) {
				navigate(
					`/establishment/summary/fuel/${establishmentActive.value}`
				);
			}
		}
	}, [establishmentId, establishmentActive, navigate]);

	const isMediumOrLarger = useMediaQuery({ minWidth: 768 }); // md comienza en 768px por defecto
	const legendPosition = isMediumOrLarger ? 'right' : 'bottom';

	return (
		<React.Fragment>
			<UiContent />
			<div className='page-content mb-10' style={{ paddingBottom: '90px' }}>
				<Container fluid>
					<BreadCrumb
						title={'Panel de Combustible'}
						links={breadcrumbLinks}
						icon={'icon-layout-dashboard'}
					/>
					<Grid columns={24}>
						<Grid.Col md={6} className='m-0 p-1'>
							<EstablishmentCardInfo id={establishmentId} />
						</Grid.Col>
						<Grid.Col md={6} sm={12} className='m-0 p-1'>
							<FuelTankInfoEChart
								establishmentId={establishmentId}
								title={'Saldo de Combustible'}
								minHeight={'150px'}
								maxHeight={'300px'}
							/>
						</Grid.Col>
						<Grid.Col md={12} sm={24} className='m-0 p-1'>
							<HourMeterPowerGenerationSummaryEChart
								establishmentId={establishmentId}
								title={'Horas de Uso'}
								minHeight={'150px'}
								maxHeight={'300px'}
								defaultFilters={{
									option: 'semana',
									group: 'centro',
								}}
							/>
						</Grid.Col>
						<Grid.Col md={12} sm={24} className='m-0 p-1'>
							<ConsumptionsPowerGenerationEChart
								establishmentId={establishmentId}
								title={'Consumos de Combustible'}
								legendPosition={legendPosition}
								minHeight={'150px'}
								maxHeight={'300px'}
								defaultFilters={{
									option: 'mes',
									group: 'centro',
								}}
							/>
						</Grid.Col>
						<Grid.Col md={12} sm={24} className='m-0 p-1'>
							<EmissionsPowerGenerationEChart
								establishmentId={establishmentId}
								title={'Emisiones de Combustible'}
								legendPosition={legendPosition}
								minHeight={'150px'}
								maxHeight={'300px'}
								defaultFilters={{
									option: 'mes',
									group: 'centro',
								}}
							/>
						</Grid.Col>
					</Grid>
				</Container>
			</div>
		</React.Fragment>
	);
};

export default FuelDashboard;
